import { toast } from 'sonner';
import { Inbox, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { StatsCard } from '../components/StatsCard';
import type { UploadedDocumentOut } from '../types';
import { LoadingState } from '../components/LoadingState';
import { type InboxItem, InboxRow } from '../components/InboxRow';
import { DocumentViewerSheet } from '../components/DocumentViewerSheet';
import { UploadDocumentDialog } from '../components/UploadDocumentDialog';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { UploadedDocumentInboxSheet } from '../components/UploadedDocumentInboxSheet';
import {
  type GeneratedDocumentOut,
  useAssignDocumentToPatient,
  useDeleteDocument,
  useDocuments,
} from '../api/documents-api';
import {
  useAssignUploadedDocumentToPatient,
  useDeleteUploadedDocument,
  useUploadedDocuments,
} from '../api/uploaded-documents-api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function DocumentsPage() {
  const setPageHeader = usePageHeaderSetter();
  const [selectedGenerated, setSelectedGenerated] = useState<GeneratedDocumentOut | null>(null);
  const [generatedViewerOpen, setGeneratedViewerOpen] = useState(false);
  const [selectedUploadedId, setSelectedUploadedId] = useState<string | null>(null);
  const [uploadedViewerOpen, setUploadedViewerOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<InboxItem | null>(null);

  const { data: generatedData, isLoading: generatedLoading } = useDocuments({ limit: 100, inbox: true });
  const { data: uploadedData, isLoading: uploadedLoading } = useUploadedDocuments({ limit: 100, inbox: true });

  const assignGenerated = useAssignDocumentToPatient();
  const assignUploaded = useAssignUploadedDocumentToPatient();
  const deleteGenerated = useDeleteDocument();
  const deleteUploaded = useDeleteUploadedDocument();

  useEffect(() => {
    setPageHeader({
      title: 'Inbox',
      actions: (
        <Button onClick={() => setUploadDialogOpen(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Upload to Inbox
        </Button>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const items = useMemo<InboxItem[]>(() => {
    const merged: InboxItem[] = [];
    for (const doc of generatedData?.data ?? []) {
      merged.push({ data: doc, kind: 'generated' });
    }
    for (const doc of uploadedData?.data ?? []) {
      merged.push({ data: doc, kind: 'uploaded' });
    }
    merged.sort((a, b) => sortKey(b).localeCompare(sortKey(a)));
    return merged;
  }, [generatedData, uploadedData]);

  const total = (generatedData?.meta.total_items ?? 0) + (uploadedData?.meta.total_items ?? 0);

  const handleOpen = (item: InboxItem) => {
    if (item.kind === 'generated') {
      setSelectedGenerated(item.data);
      setGeneratedViewerOpen(true);
    } else {
      setSelectedUploadedId(item.data.id);
      setUploadedViewerOpen(true);
    }
  };

  const handleAssign = async (item: InboxItem, patientId: string) => {
    try {
      if (item.kind === 'generated') {
        await assignGenerated.mutateAsync({ patient_id: patientId, documentId: item.data.id });
      } else {
        await assignUploaded.mutateAsync({ patient_id: patientId, documentId: item.data.id });
      }
      toast.success('Assigned to patient');
    } catch {
      toast.error('Failed to assign document.');
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const item = pendingDelete;
    setPendingDelete(null);
    try {
      if (item.kind === 'generated') {
        await deleteGenerated.mutateAsync(item.data.id);
      } else {
        await deleteUploaded.mutateAsync(item.data.id);
      }
      toast.success('Document deleted');
    } catch {
      toast.error('Failed to delete document.');
    }
  };

  if (generatedLoading || uploadedLoading) return <LoadingState />;

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
        <StatsCard icon={Inbox} value={total} label="Awaiting assignment" />
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border bg-card p-12 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Inbox aria-hidden className="h-6 w-6 text-primary" />
          </div>
          <h2 className="mt-3 text-lg font-semibold">Inbox is empty</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Reports generated from Quick Start and documents uploaded here will land in this Inbox until you assign them
            to a patient chart.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((item) => (
            <InboxRow
              key={`${item.kind}-${item.data.id}`}
              item={item}
              onOpen={handleOpen}
              onDelete={(it) => setPendingDelete(it)}
              onAssign={(it, patientId) => void handleAssign(it, patientId)}
              busy={
                assignGenerated.isPending ||
                assignUploaded.isPending ||
                deleteGenerated.isPending ||
                deleteUploaded.isPending
              }
            />
          ))}
        </div>
      )}

      <DocumentViewerSheet
        open={generatedViewerOpen}
        document={selectedGenerated}
        onOpenChange={setGeneratedViewerOpen}
      />
      <UploadedDocumentInboxSheet
        open={uploadedViewerOpen}
        documentId={selectedUploadedId ?? undefined}
        onOpenChange={setUploadedViewerOpen}
      />
      <UploadDocumentDialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen} />

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>
              The document will be removed from the Inbox. This action can&apos;t be undone from the UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function sortKey(item: InboxItem): string {
  if (item.kind === 'generated') return item.data.generated_at;
  return item.data.document_date;
}
